import {
  FluentProvider,
  teamsDarkTheme,
  teamsHighContrastTheme,
  teamsLightTheme,
  Theme,
  tokens,
} from '@fluentui/react-components';
import { registerIcons } from '@fluentui/style-utilities';
import { ArrowRight16Regular } from '@fluentui/react-icons';
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';

registerIcons({
  icons: {
    ArrowRight: <ArrowRight16Regular />,
  },
});

const appThemes = ['default', 'dark', 'contrast'] as const;

export type AppTheme = (typeof appThemes)[number];

const customThemes = (theme: AppTheme) => {
  if (theme === 'dark') {
    return teamsDarkTheme;
  }

  if (theme === 'contrast') {
    return teamsHighContrastTheme;
  }

  return {
    ...teamsLightTheme,
    colorNeutralBackground3: '#eeeeee',
  };
};

interface IThemeContext {
  themeName: AppTheme;
  theme: Theme;
  setTheme: (theme: AppTheme) => void;
}

const initialContext: IThemeContext = {
  themeName: 'default',
  theme: customThemes('default'),
  setTheme: (theme: AppTheme) => {},
};

export const ThemeContext = createContext<IThemeContext>(initialContext);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useThemeContext must be used within an ThemeProvider');
  }

  return context;
};

export function isAppTheme(theme: string): theme is AppTheme {
  return appThemes.includes(theme as AppTheme);
}

export const ThemeContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [themeName, setThemeName] = useState<AppTheme>('default');

  const theme = customThemes(themeName);

  const value = useMemo(() => ({ theme, themeName, setTheme: setThemeName }), [theme, themeName]);

  return (
    <ThemeContext.Provider value={value}>
      <FluentProvider
        theme={theme}
        style={{
          background: tokens.colorNeutralBackground3,
          height: '100vh',
          overflowY: 'scroll',
          paddingBottom: tokens.spacingVerticalS,
          width: '100vw',
        }}
      >
        {children}
      </FluentProvider>
    </ThemeContext.Provider>
  );
};
